/**
 * Vitals display helpers: pure functions shared by the vitals card, the entry dialog, the history,
 * trends and printout. Categories are read from the stored `interpretation`, never recomputed;
 * a set being entered is previewed with the same vitalsRules the flow uses at save.
 */
import { BODY_POSITION_EXTENSION_URL, BODY_POSITIONS, BP_COMPONENT_CODINGS, VITALS, VITALS_BY_KEY, VITALS_INTERPRETATION_SYSTEM } from '@/common/vitalsConstants';
import {
    ageInYears,
    bmiCategory,
    bpCategory,
    calculateBmi,
    calculatePercentOf,
    checkPlausibility,
    feetInchesToInches,
    interpretationFor,
    isBlank,
    isOrthostaticDrop,
    orthostaticReference,
    roundTo1,
} from '@/common/vitalsRules';

export const CATEGORY_COLORS = {
    underweight: 'info',
    'bp-low': 'info',
    normal: 'success',
    'bp-normal': 'success',
    // Classic's yellow: the warning token is orange, the same as Stage 1.
    'bp-elevated': 'amber-darken-1',
    overweight: 'orange-darken-1',
    'bp-stage-1': 'orange-darken-1',
    obesity: 'deep-orange-darken-2',
    'bp-stage-2': 'deep-orange-darken-2',
    'bp-crisis': 'error',
    'orthostatic-drop': 'error',
};

// The hex of each CATEGORY_COLORS value (docs/design/design-system.md, Vitals category colors), for charts and print.
export const CATEGORY_HEX = {
    info: '#2196F3',
    success: '#43A047',
    'amber-darken-1': '#FFB300',
    'orange-darken-1': '#FB8C00',
    'deep-orange-darken-2': '#E64A19',
    error: '#C62828',
};

/** What formatReading returns for a reading with no value. */
export const EMPTY_READING = '—';

function componentValue(observation, part) {
    const code = BP_COMPONENT_CODINGS[part].code;
    return (observation?.component || []).find((component) => component?.code?.coding?.some((coding) => coding?.code === code))?.value?.Quantity?.value;
}

/** Inches as height is entered and read, e.g. 67 -> '5 ft 7 in'. */
export function formatHeight(inches) {
    let feet = Math.floor(inches / 12);
    let remainder = roundTo1(inches - feet * 12);
    if (remainder >= 12) {
        feet += 1;
        remainder = roundTo1(remainder - 12);
    }
    return `${feet} ft ${remainder} in`;
}

export function formatReading(key, observation) {
    const vital = VITALS_BY_KEY[key];
    if (!vital) return EMPTY_READING;

    if (vital.kind === 'bloodPressure') {
        const systolic = componentValue(observation, 'systolic');
        const diastolic = componentValue(observation, 'diastolic');
        return isBlank(systolic) || isBlank(diastolic) ? EMPTY_READING : `${systolic}/${diastolic} ${vital.measurementLabel}`;
    }

    const value = observation?.value?.Quantity?.value;
    if (isBlank(value) || !Number.isFinite(Number(value))) return EMPTY_READING;
    if (key === 'height') return formatHeight(Number(value));
    return `${value} ${vital.measurementLabel}`;
}

/** A saved reading as the entry dialog's fields take it: a number, { feet, inches } or { systolic, diastolic }; undefined when it has no value. */
export function entryValueFor(key, observation) {
    const vital = VITALS_BY_KEY[key];
    if (!vital) return undefined;

    if (vital.kind === 'bloodPressure') {
        const systolic = componentValue(observation, 'systolic');
        const diastolic = componentValue(observation, 'diastolic');
        return isBlank(systolic) || isBlank(diastolic) ? undefined : { systolic, diastolic };
    }

    const value = observation?.value?.Quantity?.value;
    if (isBlank(value) || !Number.isFinite(Number(value))) return undefined;
    if (key !== 'height') return Number(value);
    const feet = Math.floor(Number(value) / 12);
    return { feet, inches: roundTo1(Number(value) - feet * 12) };
}

/** Who last saved the set: the agent of its newest Provenance, else the person who recorded it. */
export function lastEditor(set) {
    const provenances = [...Object.values(set?.provenanceByReadingId || {}).flat(), ...(set?.panelProvenance || [])];
    const recordedAt = (provenance) => new Date(provenance?.recorded || 0).getTime() || 0;
    const newest = provenances.reduce((latest, provenance) => (!latest || recordedAt(provenance) > recordedAt(latest) ? provenance : latest), null);
    return newest?.agent?.[0]?.who?.display || set?.panel?.performer?.[0]?.display || null;
}

const compactQuantity = (value) => (value?.Quantity ? { Quantity: { value: value.Quantity.value } } : undefined);

/**
 * A set cut down to what the Vitals widget and its print read back: when it was taken, who
 * recorded it and who last saved it, its note and each reading's value. The widget's history mode
 * stores a whole range of sets as the form's answer, which is too heavy to keep as full resources
 * with their edit history. lastEditor, formatReading and the note read the same from the copy.
 */
export function compactVitalsSet(set) {
    const panel = set?.panel || {};
    const performer = panel.performer?.[0]?.display;
    const editor = lastEditor(set);
    const note = panel.note?.[0]?.text;
    return {
        panel: {
            id: panel.id,
            effective: { dateTime: panel.effective?.dateTime },
            ...(performer ? { performer: [{ display: performer }] } : {}),
            ...(note ? { note: [{ text: note }] } : {}),
        },
        readings: Object.fromEntries(
            Object.entries(set?.readings || {}).map(([key, observation]) => [
                key,
                {
                    id: observation?.id,
                    value: compactQuantity(observation?.value),
                    component: observation?.component?.map((part) => ({
                        code: { coding: (part?.code?.coding || []).map(({ system, code }) => ({ system, code })) },
                        value: compactQuantity(part?.value),
                    })),
                },
            ]),
        ),
        provenanceByReadingId: {},
        // Only who last saved the set, and only when that isn't who recorded it.
        panelProvenance: editor && editor !== performer ? [{ agent: [{ who: { display: editor } }] }] : [],
    };
}

export function readingFlags(observation) {
    return (observation?.interpretation || []).flatMap((concept) =>
        (concept?.coding || [])
            .filter((coding) => coding?.system === VITALS_INTERPRETATION_SYSTEM)
            .map((coding) => ({ code: coding.code, display: coding.display, color: CATEGORY_COLORS[coding.code] })),
    );
}

// An entered value as a number (inches for height) or { systolic, diastolic } with both numbers; undefined otherwise.
function enteredValue(key, value) {
    const vital = VITALS_BY_KEY[key];
    if (vital.kind === 'bloodPressure') {
        if (isBlank(value?.systolic) || isBlank(value?.diastolic)) return undefined;
        const systolic = Number(value.systolic);
        const diastolic = Number(value.diastolic);
        return Number.isFinite(systolic) && Number.isFinite(diastolic) ? { systolic, diastolic } : undefined;
    }
    if (key === 'height' && value !== null && typeof value === 'object') {
        if (isBlank(value.feet) && isBlank(value.inches)) return undefined;
        return feetInchesToInches(value.feet, value.inches);
    }
    if (isBlank(value)) return undefined;
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
}

const isEntered = (key, value) => {
    if (VITALS_BY_KEY[key].kind === 'bloodPressure') return !isBlank(value?.systolic) || !isBlank(value?.diastolic);
    if (key === 'height' && value !== null && typeof value === 'object') return !isBlank(value.feet) || !isBlank(value.inches);
    return !isBlank(value);
};

const flagsFor = (category, options) => readingFlags({ interpretation: interpretationFor(category, options) });

/** A live preview of a set being entered: the calculated values, category flags and soft plausibility warnings. */
export function previewSet(readings, { birthDate, effectiveDateTime } = {}) {
    const values = {};
    const warnings = {};
    VITALS.filter((vital) => !vital.calculatedFrom).forEach((vital) => {
        const raw = readings?.[vital.key];
        const value = enteredValue(vital.key, raw);
        if (value !== undefined) values[vital.key] = value;
        if (isEntered(vital.key, raw)) {
            const check = checkPlausibility(vital.key, raw);
            if (!check.plausible) warnings[vital.key] = check.message;
        }
    });

    const calculated = {};
    VITALS.filter((vital) => vital.calculatedFrom).forEach((vital) => {
        const inputs = vital.calculatedFrom.map((key) => values[key]);
        calculated[vital.key] = vital.key === 'bmi' ? calculateBmi(...inputs) : calculatePercentOf(...inputs);
    });

    const flags = {};
    const bmiFlags = flagsFor(bmiCategory(calculated.bmi, ageInYears(birthDate, effectiveDateTime)));
    if (bmiFlags.length) flags.bmi = bmiFlags;

    const referenceKey = orthostaticReference({ bpLying: values.bpLying, bpSitting: values.bpSitting });
    VITALS.filter((vital) => vital.kind === 'bloodPressure' && values[vital.key]).forEach((vital) => {
        const bp = values[vital.key];
        const orthostaticDrop = vital.key === 'bpStanding' && !!referenceKey && isOrthostaticDrop(bp, values[referenceKey]);
        const bpFlags = flagsFor(bpCategory(bp.systolic, bp.diastolic), { orthostaticDrop });
        if (bpFlags.length) flags[vital.key] = bpFlags;
    });

    return { calculated, flags, warnings };
}

const DAY_MS = 24 * 60 * 60 * 1000;
const PRESET_DAYS = { '30d': 30, '90d': 90 };

// A 'YYYY-MM-DD' date as local midnight; null when it isn't a real date.
function localDay(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
}

/** The fetchHistory bounds for the last `days` days up to `now`: { from } as a full-precision ISO string. */
export function lastDaysRange(days, now = new Date()) {
    return { from: new Date(now.getTime() - days * DAY_MS).toISOString() };
}

/** The fetchHistory bounds for a range preset: { from?, to? } as full-precision ISO strings, or null for an invalid range. */
export function historyRange(preset, { from, to } = {}, now = new Date()) {
    if (PRESET_DAYS[preset]) return lastDaysRange(PRESET_DAYS[preset], now);
    if (preset === '1y') {
        const start = new Date(now.getTime());
        start.setFullYear(start.getFullYear() - 1);
        return { from: start.toISOString() };
    }
    if (preset === 'all') return {};
    if (preset !== 'custom') return null;

    const start = localDay(from);
    const end = localDay(to);
    if (!start || !end || start > end) return null;
    end.setDate(end.getDate() + 1);
    return { from: start.toISOString(), to: end.toISOString() };
}

/** Where a set came from: its note (by form response id), a deleted note, or the chart. */
export function setSource(set) {
    const derivedFrom = set?.panel?.derivedFrom || [];
    const note = derivedFrom.find((reference) => reference?.resourceType === 'QuestionnaireResponse' && reference?.id);
    if (note) return { kind: 'note', questionnaireResponseId: note.id };
    if (derivedFrom.some((reference) => reference?.display === 'Deleted note' && !reference?.id)) return { kind: 'deleted-note' };
    return { kind: 'chart' };
}

const activityOf = (provenance) => provenance?.activity?.coding?.find((coding) => coding?.code)?.code;
const recordedTime = (provenance) => new Date(provenance?.recorded || 0).getTime() || 0;
const withActivity = (provenances, activity) => (provenances || []).filter((provenance) => activityOf(provenance) === activity).sort((a, b) => recordedTime(b) - recordedTime(a));
const changeOf = (provenance) => ({ by: provenance?.agent?.[0]?.who?.display, at: provenance?.recorded, reason: provenance?.reason?.[0]?.text });

/** How a reading changed since the set was recorded: corrected (its newest UPDATE), added by an edit (CREATE), or null. */
export function readingChange(set, key) {
    const reading = set?.readings?.[key];
    if (!reading) return null;
    const provenances = set?.provenanceByReadingId?.[reading.id] || [];
    const [update] = withActivity(provenances, 'UPDATE');
    if (update) return { kind: 'corrected', ...changeOf(update), revisionOf: update.entity?.[0]?.what?.reference };
    const [create] = withActivity(provenances, 'CREATE');
    return create ? { kind: 'added', ...changeOf(create) } : null;
}

/** Readings removed from the set by an edit, newest first. `key` is null: only the fetched original says which vital it was. */
export function removedReadings(set) {
    return withActivity(set?.panelProvenance, 'DELETE').map((provenance) => ({ key: null, ...changeOf(provenance), revisionOf: provenance.entity?.[0]?.what?.reference }));
}

/** Edits of the set as a whole, newest first. */
export function setEdits(set) {
    return withActivity(set?.panelProvenance, 'UPDATE').map(changeOf);
}

/** Every `_history` reference whose original the history shows: corrected and removed readings, de-duplicated. */
export function originalReferences(sets) {
    const references = (sets || []).flatMap((set) => [
        ...Object.keys(set?.readings || {})
            .map((key) => readingChange(set, key))
            .filter((change) => change?.kind === 'corrected')
            .map((change) => change.revisionOf),
        ...removedReadings(set).map((removed) => removed.revisionOf),
    ]);
    return [...new Set(references.filter(Boolean))];
}

function bodyPositionCode(observation) {
    const extension = (observation?.extension || []).find((item) => item?.url === BODY_POSITION_EXTENSION_URL);
    return (extension?.value?.CodeableConcept || extension?.valueCodeableConcept)?.coding?.[0]?.code;
}

/** The catalog key of a saved reading: its primary code, then its body position for blood pressure and heart rate. */
export function vitalKeyFor(observation) {
    const codes = (observation?.code?.coding || []).map((coding) => coding?.code);
    const candidates = VITALS.filter((vital) => codes.includes(vital.codings[0].code));
    if (candidates.length <= 1) return candidates[0]?.key || null;
    const position = bodyPositionCode(observation);
    return (candidates.find((vital) => BODY_POSITIONS[vital.position]?.code === position) || candidates.find((vital) => vital.position === 'sitting') || candidates[0]).key;
}

/** For a standing blood pressure flagged as an orthostatic drop: the reading it was compared with, e.g. 'Lying blood pressure 118/76 mmHg'. */
export function comparedReadingText(set, key) {
    const reading = set?.readings?.[key];
    if (key !== 'bpStanding' || !readingFlags(reading).some((flag) => flag.code === 'orthostatic-drop')) return null;
    const comparedIds = (reading.derivedFrom || []).filter((reference) => reference?.resourceType === 'Observation' && reference?.id).map((reference) => reference.id);
    const compared = Object.entries(set.readings).find(([, sibling]) => comparedIds.includes(sibling?.id));
    return compared ? `${VITALS_BY_KEY[compared[0]].label} ${formatReading(compared[0], compared[1])}` : null;
}

const BP_TREND_POSITIONS = [
    { key: 'bpSitting', name: 'Sitting' },
    { key: 'bpStanding', name: 'Standing' },
    { key: 'bpLying', name: 'Lying' },
];

// A reading's category hex (its first flag other than orthostatic drop), or undefined.
function categoryHex(observation) {
    const category = readingFlags(observation).find((flag) => flag.code !== 'orthostatic-drop');
    return category ? CATEGORY_HEX[CATEGORY_COLORS[category.code]] : undefined;
}

// One series' points, oldest first, at the time taken. A set without a value adds no point.
function trendPoints(sets, key, valueOf, colored) {
    return (sets || [])
        .map((set) => {
            const reading = set?.readings?.[key];
            const y = reading ? valueOf(reading) : undefined;
            const x = new Date(set?.panel?.effective?.dateTime).getTime();
            if (isBlank(y) || !Number.isFinite(Number(y)) || !Number.isFinite(x)) return null;
            const point = { x, y: Number(y) };
            const fillColor = colored ? categoryHex(reading) : undefined;
            if (fillColor) point.fillColor = fillColor;
            return point;
        })
        .filter(Boolean)
        .sort((a, b) => a.x - b.x);
}

/** A vital's trend as line series. Blood pressure is a systolic and diastolic pair per position, never mixed. */
export function trendSeries(sets, key, { withPercentIdeal } = {}) {
    if (key === 'bloodPressure') {
        const series = BP_TREND_POSITIONS.filter((position) => (sets || []).some((set) => set?.readings?.[position.key])).flatMap((position) => [
            { name: `${position.name} systolic`, data: trendPoints(sets, position.key, (reading) => componentValue(reading, 'systolic'), true) },
            { name: `${position.name} diastolic`, data: trendPoints(sets, position.key, (reading) => componentValue(reading, 'diastolic'), true) },
        ]);
        return { series, yAxes: 1 };
    }

    const quantity = (reading) => reading?.value?.Quantity?.value;
    const series = [{ name: VITALS_BY_KEY[key]?.label || key, data: trendPoints(sets, key, quantity, key === 'bmi') }];
    if (key !== 'weight' || !withPercentIdeal) return { series, yAxes: 1 };
    series.push({ name: '% of ideal body weight', data: trendPoints(sets, 'percentIdealBodyWeight', quantity, false) });
    return { series, yAxes: 2 };
}
